/**
 * The docs role-visibility rules, as plain ESM with no build step of its
 * own. Two very different consumers need these exact rules and must never
 * drift:
 *
 *   - `docAccess.js` — the browser-side gate (sidebar entries, whether
 *     `DocsMdxView` serves a page on direct navigation), which wraps these
 *     with `hasRole` and exposes `canViewDoc`.
 *   - `scripts/build-docs-search-index.mjs` — the Node build script that
 *     shards the search index by audience, run by plain `node` with no
 *     bundler in the chain.
 *
 * Keeping the data here, importable from both Node and the browser, is
 * what stops a page from being gated one way in the nav and indexed
 * another way in search — the class of bug this file exists to remove.
 *
 * See `docAccess.js` for the full rationale, including why this is a
 * nav-parity boundary and not a confidentiality one.
 */

/** Every audience, narrowest readership last. Order is not significant. */
export const DOC_AUDIENCES = [
    'member',
    'leadership',
    'org_admin',
    'any_admin',
    'partner',
    'site_admin'
]

// Role strings must match src/features/docs/docsRoles.js / what the API
// puts in user.roles.
export const SITE_ADMIN_ROLE = 'Site Admin Role'

// null = any authenticated docs reader. [] = site admins only.
// Site Admin Role short-circuits every audience and is therefore never
// listed.
export const AUDIENCE_ROLES = {
    member: null,
    leadership: ['Slt Role', 'Admin Role'],
    org_admin: ['Admin Role'],
    any_admin: ['Admin Role', 'Partner Admin Role'],
    partner: ['Partner Admin Role'],
    site_admin: []
}

/**
 * Exact-path rulings that contradict their directory's prefix rule.
 * Checked before `AUDIENCE_PREFIXES`. Keep this list short — an entry
 * here is a page filed in the wrong place, and moving the file is
 * usually the better fix.
 *
 * `docs/power/` deliberately mixes operator reference (welcome, insights,
 * roles, faq) with reader guides, so its guides need per-page rulings
 * rather than a prefix rule. `site-admin/data-quality` is the one page in
 * the Platform Operations section the sidebar left at the section's own
 * `['Site Admin Role', 'Admin Role']` rather than narrowing to site
 * admins, so it keeps the wider `org_admin` audience here too.
 */
export const AUDIENCE_EXACT = {
    '/docs/power/user-guide': 'member',
    '/docs/power/home-user-guide': 'member',
    '/docs/power/targets-user-guide': 'member',
    '/docs/power/group-dashboard-user-guide': 'member',
    // Read by the staff roles the league page admits (property-league-and-house-standings).
    '/docs/power/property-league-user-guide': 'member',
    '/docs/power/activities-user-guide': 'member',
    '/docs/power/houses-and-groups-user-guide': 'member',
    '/docs/power/explore-and-insights-user-guide': 'leadership',
    '/docs/power/finance-user-guide': 'leadership',
    '/docs/power/carbon-reporting-user-guide': 'leadership',
    '/docs/power/budget-user-guide': 'org_admin',
    '/docs/power/meter-categories-user-guide': 'org_admin',
    '/docs/power/school-calendar-user-guide': 'org_admin',
    '/docs/power/tenancies-user-guide': 'org_admin',
    '/docs/power/location-access-user-guide': 'org_admin',
    '/docs/power/change-of-tenancy-user-guide': 'org_admin',
    '/docs/power/recharges-user-guide': 'org_admin',
    '/docs/power/contracts-user-guide': 'org_admin',
    '/docs/power/statements-user-guide': 'org_admin',
    '/docs/power/adding-people': 'any_admin',
    // Admins who sign in with a password must use it, so it sits with the
    // other admin housekeeping. Everyone else can turn it on from the Security
    // tab, whose own copy explains it.
    '/docs/power/two-factor-authentication-user-guide': 'any_admin',
    '/docs/power/terms-and-partner-access': 'any_admin',
    '/docs/power/what-we-need-from-you': 'any_admin',
    '/docs/site-admin/data-quality': 'org_admin'
}

/**
 * Directory rules, longest match wins. Ordering in this array does not
 * matter; `resolveDocAudience` sorts by specificity so `/docs/partners/`
 * beats `/docs/partner/`.
 */
export const AUDIENCE_PREFIXES = [
    ['/docs/partner/', 'partner'],
    ['/docs/partners/', 'site_admin'],
    ['/docs/alerts/', 'org_admin'],
    ['/docs/utility-fetchers/', 'org_admin'],
    ['/docs/power/', 'site_admin'],
    ['/docs/developer/', 'site_admin'],
    ['/docs/ops/', 'site_admin'],
    ['/docs/planning/', 'site_admin'],
    ['/docs/site-admin/', 'site_admin'],
    ['/docs/support/', 'site_admin']
]

const SORTED_PREFIXES = [...AUDIENCE_PREFIXES].sort(
    (a, b) => b[0].length - a[0].length
)

/**
 * Directories a docs page may live in. `--check` in
 * `scripts/build-docs-search-index.mjs` asserts every on-disk MDX file
 * sits in one of these, so adding e.g. `frontend/docs/finance/` fails the
 * check instead of silently inheriting the `site_admin` default and
 * shipping with the narrowest possible readership unnoticed. Add the
 * directory here *and* give it a rule in `AUDIENCE_PREFIXES`.
 */
export const KNOWN_DOC_DIRS = new Set([
    '/docs/alerts',
    '/docs/developer',
    '/docs/ops',
    '/docs/partner',
    '/docs/partners',
    '/docs/planning',
    '/docs/power',
    '/docs/site-admin',
    '/docs/support',
    '/docs/utility-fetchers'
])

/** Last path segment is `technical` or ends `-technical`. */
const TECHNICAL_BASENAME = /(^|-)technical$/

/** Resolve a `/docs/...` path (no `.mdx` suffix) to its audience. */
export function resolveDocAudience(path) {
    const exact = AUDIENCE_EXACT[path]
    if (exact) return exact

    // The `-technical` convention. Checked BEFORE prefixes, because
    // `/docs/alerts/technical` and `/docs/utility-fetchers/*-technical`
    // sit under `org_admin`-gated directories but are Developer Reference
    // pages. This is a real convention in this repo — all 12 `*technical`
    // pages sit in the site-admin-gated Developer Reference sidebar
    // sections — so it is one rule rather than 12 exact rulings.
    const basename = path.split('/').pop()
    if (TECHNICAL_BASENAME.test(basename)) return 'site_admin'

    const prefix = SORTED_PREFIXES.find(([p]) => path.startsWith(p))
    if (prefix) return prefix[1]

    // An unrecognised path is refused rather than admitted. `site_admin`
    // is the narrowest audience available, so an unmapped page becomes
    // visible to the smallest possible readership instead of the largest
    // — a missing rule should show up as "only site admins can see it",
    // never as a leak.
    return 'site_admin'
}
